/**
 * Telegram Mini App SDK initialisation.
 * VITE_API_BASE — set in .env (see .env.example)
 *
 * Safe to run outside Telegram (dev browser) — detects missing env and mocks.
 */

import {
  isTMA,
  init,
  mountMiniApp,
  miniAppReady,
  mountThemeParams,
  bindThemeParamsCssVars,
  mountViewport,
  bindViewportCssVars,
  expandViewport,
  disableClosingConfirmation,
  setMiniAppHeaderColor,
  hapticFeedbackImpactOccurred,
  mockTelegramEnv,
  retrieveLaunchParams,
  openLink as tgOpenLink,
} from "@telegram-apps/sdk-react";

let _tmaAvailable = false;

export function isTelegramAvailable(): boolean {
  return _tmaAvailable;
}

export async function initTelegramApp(): Promise<void> {
  // isTMA() with no args is synchronous — true if Telegram env is present
  const inTelegram = isTMA();
  _tmaAvailable = inTelegram;

  if (!inTelegram) {
    mockTelegramEnv({
      launchParams: {
        tgWebAppVersion: "7.0",
        tgWebAppPlatform: "web",
        tgWebAppThemeParams: {},
      },
    });
  }

  try {
    init();
    await mountMiniApp();
    try {
      miniAppReady();
    } catch {
      // older SDK / not supported — ignore
    }
    mountThemeParams();
    bindThemeParamsCssVars();

    try {
      setMiniAppHeaderColor("#313131");
    } catch {
      // not supported in this version — ignore
    }

    try {
      await mountViewport();
      bindViewportCssVars();
    } catch {
      // ignore if unsupported
    }

    try {
      expandViewport();
    } catch {
      // ignore if unsupported
    }

    try {
      disableClosingConfirmation();
    } catch {
      // ignore
    }
  } catch (e) {
    console.warn("[TG init] SDK init error (non-fatal):", e);
  }
}

/** Light haptic tap — call on interactive taps. */
export function hapticTap(): void {
  try {
    hapticFeedbackImpactOccurred("light");
  } catch {
    // ignore outside Telegram
  }
}

/** Open a URL via Telegram's native openLink (or window.open fallback). */
export function openLink(url: string): void {
  try {
    if (_tmaAvailable) {
      tgOpenLink(url);
    } else {
      window.open(url, "_blank", "noopener");
    }
  } catch {
    window.open(url, "_blank", "noopener");
  }
}

export { retrieveLaunchParams };
